import { HttpClient } from 'api/http-client';

export interface CustomerPortalTheme {
  customerPortalThemeId: number;
  customerPortalThemeName: string;
  brandNameOrWebsite?: string;
  themeDefinition: CustomerPortalThemeDefinition;
}

export interface CustomerPortalThemeDefinition {
  fonts: CustomerPortalFont[];
  colors: CustomerPortalThemeColor[];
  palette: CustomerPortalThemeColor[];
  typography: CustomerPortalTypographyConfig[];
  buttons: CustomerPortalButtonConfig[];
}

export enum FontType {
  CUSTOM = 'CUSTOM',
  GOOGLE = 'GOOGLE',
}

export enum CustomerPortalButtonType {
  Primary = 'PRIMARY',
  Secondary = 'SECONDARY',
  Tertiary = 'TERTIARY',
}

export enum CustomerPortalButtonShape {
  Pill = 'PILL',
  Rounded = 'ROUNDED',
  Square = 'SQUARE',
}

export const CustomerPortalButtonShapeRadius: {
  [name in CustomerPortalButtonShape]: string;
} = {
  [CustomerPortalButtonShape.Square]: '0rem',
  [CustomerPortalButtonShape.Rounded]: '0.5rem',
  [CustomerPortalButtonShape.Pill]: '3rem',
};

export interface CustomerPortalButtonConfig {
  buttonType: CustomerPortalButtonType;
  fontName: string;
  weight: string;
  size: string;
  shape: CustomerPortalButtonShape;
  backgroundColor: CustomerPortalThemeColor;
  textColor: CustomerPortalThemeColor;
  borderColor: CustomerPortalThemeColor;
  outline: string;
}

export interface CustomerPortalFont {
  fontType: FontType;
  fontUrl: string;
  cssUrl: string;
  family: string;
  variants: [];
  subsets: [];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  category: string;
  kind: string;
  menu: string;
}

export interface CustomerPortalThemeColor {
  color: string;
  name: string;
  label: string;
  default?: boolean;
}

export enum CustomerPortalTypographyType {
  Heading1 = 'HEADING_1',
  Heading2 = 'HEADING_2',
  Heading3 = 'HEADING_3',
  Heading4 = 'HEADING_4',
  Heading5 = 'HEADING_5',
  Heading6 = 'HEADING_6',
  Heading7 = 'HEADING_7',
  ParaLarge = 'PARA_LARGE',
  ParaMedium = 'PARA_MEDIUM',
  ParaSmall = 'PARA_SMALL',
  Link = 'LINK',
  Caption = 'CAPTION',
  Quote = 'QUOTE',
}

export interface CustomerPortalTypographyConfig {
  typographyType: CustomerPortalTypographyType;
  weight: string;
  fontName: string;
  size: string;
}

export type GetThemeFromWebsitePayload = {
  website: string;
};

type UpdateThemePayload = {
  payload: Omit<CustomerPortalTheme, 'customerPortalThemeId'>;
  id: number;
};

export interface GoogleFontsResponse {
  kind: string;
  items: CustomerPortalFont[];
}

class CPThemeHttpClient extends HttpClient {
  public getAllThemes() {
    return this.get<CustomerPortalTheme[]>('/customer-portal-theme');
  }

  public getThemeById(id: number) {
    return this.get<CustomerPortalTheme>(`/customer-portal-theme/${id}`);
  }

  public deleteThemeById(id: number) {
    return this.delete<CustomerPortalTheme>(`/customer-portal-theme/${id}`);
  }

  public createTheme(
    payload: Omit<CustomerPortalTheme, 'customerPortalThemeId'>,
  ): Promise<CustomerPortalTheme> {
    return this.post('/customer-portal-theme', payload);
  }

  public getThemeFromWebsite(
    payload: GetThemeFromWebsitePayload,
  ): Promise<CustomerPortalTheme> {
    return this.post('/customer-portal-theme/portal-from-website', payload);
  }

  public updateTheme({
    id,
    payload,
  }: UpdateThemePayload): Promise<CustomerPortalTheme> {
    return this.put(`/customer-portal-theme/${id}`, payload);
  }

  public getAvailableFonts(): Promise<GoogleFontsResponse> {
    return this.get('/customer-portal-theme/available-fonts');
  }

  public getVariantsForFonts(
    family: string,
  ): Promise<Pick<CustomerPortalFont, 'variants'>> {
    return this.get('/customer-portal-theme/variants-for-font', {
      params: { family },
    });
  }

  public getPortalsCountForTheme(id: number): Promise<{
    themeId: number;
    count: number;
  }> {
    return this.post(`/customer-portal-theme/${id}/portals/count`);
  }
}

export const CPThemeApi = new CPThemeHttpClient();
